import React from 'react';
import Card from './Card';

export interface StatCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Title of the stat card */
  title: string;
  /** Value to display */
  value: string | number;
  /** Optional subtitle or description */
  subtitle?: string;
  /** Icon to display */
  icon?: React.ReactNode;
  /** Change indicator */
  change?: number;
  /** Trend direction */
  trend?: 'up' | 'down' | 'neutral';
  /** Whether trend direction is positive (regardless of up/down) */
  isPositive?: boolean;
  /** Color variant */
  variant?: 'default' | 'primary' | 'success' | 'warning' | 'danger' | 'info';
  /** Loading state */
  loading?: boolean;
  /** Format for the value */
  format?: 'default' | 'currency' | 'percent' | 'decimal';
  /** Position of the icon */
  iconPosition?: 'left' | 'top';
  /** Whether to display a chart */
  chart?: React.ReactNode;
  /** Custom action button or link */
  action?: React.ReactNode;
}

export const StatCard = React.forwardRef<HTMLDivElement, StatCardProps>(
  (
    {
      title,
      value,
      subtitle,
      icon,
      change,
      trend,
      isPositive,
      variant = 'default',
      loading = false,
      format = 'default',
      iconPosition = 'left',
      chart,
      action,
      className = '',
      ...props
    },
    ref
  ) => {
    // Format value based on format prop
    const formatValue = (val: string | number) => {
      if (typeof val === 'string') return val;
      
      switch (format) {
        case 'currency':
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(val);
        case 'percent':
          return `${val}%`;
        case 'decimal':
          return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(val);
        default:
          return new Intl.NumberFormat().format(val);
      }
    };
    
    // Determine color classes for the trend indicator
    const getTrendColor = () => {
      if (trend === 'up') {
        return isPositive ? 'text-green-500' : 'text-red-500';
      }
      if (trend === 'down') {
        return isPositive ? 'text-red-500' : 'text-green-500';
      }
      return 'text-gray-500';
    };
    
    // Trend indicator icon
    const renderTrendIcon = () => {
      if (trend === 'up') {
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-4 h-4"
          >
            <path
              fillRule="evenodd"
              d="M12 7a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0V8.414l-4.293 4.293a1 1 0 01-1.414 0L8 10.414l-4.293 4.293a1 1 0 01-1.414-1.414l5-5a1 1 0 011.414 0L11 10.586 14.586 7H12z"
              clipRule="evenodd"
            />
          </svg>
        );
      }
      if (trend === 'down') {
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
            className="w-4 h-4"
          >
            <path
              fillRule="evenodd"
              d="M12 13a1 1 0 100 2h5a1 1 0 001-1V9a1 1 0 10-2 0v2.586l-4.293-4.293a1 1 0 00-1.414 0L8 9.586 3.707 5.293a1 1 0 00-1.414 1.414l5 5a1 1 0 001.414 0L11 9.414 14.586 13H12z"
              clipRule="evenodd"
            />
          </svg>
        );
      }
      return null;
    };
    
    // Variant colors for icon container
    const variantIconClasses = {
      default: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
      primary: 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
      success: 'bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-300',
      warning: 'bg-yellow-100 text-yellow-600 dark:bg-yellow-900/30 dark:text-yellow-300',
      danger: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300',
      info: 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300',
    };
    
    // Skeleton loader for loading state
    if (loading) {
      return (
        <div ref={ref} {...props}>
          <Card className={`overflow-hidden ${className}`}>
            <div className="flex items-center p-4">
              {icon && iconPosition === 'left' && (
                <div className="mr-4 rounded-full w-12 h-12 bg-gray-200 dark:bg-gray-700 animate-pulse" />
              )}
              <div className="flex-1">
                <div className="h-4 w-1/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-3" />
                <div className="h-8 w-1/2 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                {subtitle && (
                  <div className="h-4 w-2/3 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mt-2" />
                )}
              </div>
            </div>
            {chart && (
              <div className="h-20 bg-gray-100 dark:bg-gray-800 animate-pulse" />
            )}
          </Card>
        </div>
      );
    }
    
    return (
      <div ref={ref} {...props}>
        <Card className={`overflow-hidden ${className}`}>
        <div className={`p-4 ${chart ? 'pb-0' : ''}`}>
          {/* Layout with icon on top */}
          {icon && iconPosition === 'top' && (
            <div className="flex flex-col items-start">
              <div className={`p-2 rounded-lg mb-3 ${variantIconClasses[variant]}`}>
                <div className="w-5 h-5">{icon}</div>
              </div>
              <div className="flex-1 w-full">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</h3>
                <div className="flex items-baseline mt-1">
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {formatValue(value)}
                  </p>
                  {change !== undefined && (
                    <p className={`ml-2 flex items-center text-sm ${getTrendColor()}`}>
                      {renderTrendIcon()}
                      <span className="ml-1">{Math.abs(change)}%</span>
                    </p>
                  )}
                </div>
                {subtitle && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
                )}
              </div>
            </div>
          )}
          
          {/* Layout with icon on left or no icon */}
          {(!icon || iconPosition === 'left') && (
            <div className="flex items-start">
              {icon && iconPosition === 'left' && (
                <div className={`p-3 rounded-lg mr-4 ${variantIconClasses[variant]}`}>
                  <div className="w-6 h-6">{icon}</div>
                </div>
              )}
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</h3>
                <div className="flex items-baseline mt-1">
                  <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {formatValue(value)}
                  </p>
                  {change !== undefined && (
                    <p className={`ml-2 flex items-center text-sm ${getTrendColor()}`}>
                      {renderTrendIcon()}
                      <span className="ml-1">{Math.abs(change)}%</span>
                    </p>
                  )}
                </div>
                {subtitle && (
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{subtitle}</p>
                )}
              </div>
            </div>
          )}
          
          {/* Action button/link */}
          {action && (
            <div className="mt-2">{action}</div>
          )}
        </div>
        
        {/* Optional chart */}
        {chart && (
          <div className="mt-2">{chart}</div>
        )}
        </Card>
      </div>
    );
  }
);

StatCard.displayName = 'StatCard';

export default StatCard;